import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Grid, Icons } from '../../';
import styles from './build-quote.module.scss';

const TOTAL_STEPS = 3;
const STEP_TIME = 3000;

const BuildQuote = ({
  loading,
  title,
  stepOneMessage,
  stepTwoMessage,
  stepThreeMessage,
  ...props
}) => {
  const stepsRef = useRef();
  const stepRef = useRef(1);
  const intervalIDRef = useRef();
  const [step, setStep] = useState(1);
  const [allCompleted, setAllCompleted] = useState(true);

  const increment = () => {
    // access the current state value
    const newStep = stepRef.current + 1;
    stepRef.current = newStep;
    if (stepRef.current > TOTAL_STEPS) {
      clearInterval(intervalIDRef.current);
    }
    stepRef.current = newStep;
    setStep(newStep);
    stepsRef.current?.style.setProperty(
      '--transform-offset',
      `-${((newStep > 2 ? 2 : newStep - 1) / 3) * 100}%`
    );
  };

  useEffect(() => {
    return () => intervalIDRef.current && clearInterval(intervalIDRef.current);
  }, []);

  useEffect(() => {
    if (!loading) return;
    setAllCompleted(false);
    if (step > 1) {
      setStep(1);
      stepRef.current = 1;
    }
    const id = setInterval(increment, STEP_TIME);
    intervalIDRef.current = id;
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (allCompleted) return null;

  return (
    // eslint-disable-next-line react/prop-types
    <Grid className={props.className}>
      <Grid.Row justifyContentCenter>
        <div
          className={cn(styles.buildQuote, {
            [styles.allCompleted]: !loading,
          })}
          onAnimationEnd={() => setAllCompleted(true)}
        >
          <h1 className={styles.title}>{title}</h1>
          <div ref={stepsRef} className={styles.steps}>
            <div
              className={cn(styles.step, {
                [styles.completed]: step > 1,
                [styles.current]: step === 1,
              })}
            >
              <div className={styles.iconContainer}>
                {step > 1 && <Icons.CheckCircle className={styles.icon} />}
                {step === 1 && <Icons.NextArrow className={styles.icon} />}
              </div>
              <span>{stepOneMessage}</span>
            </div>
            <div
              className={cn(styles.step, {
                [styles.completed]: step > 2,
                [styles.current]: step === 2,
              })}
            >
              <div className={styles.iconContainer}>
                {step > 2 && <Icons.CheckCircle className={styles.icon} />}
                {step === 2 && <Icons.NextArrow className={styles.icon} />}
              </div>
              <span>{stepTwoMessage}</span>
            </div>
            <div
              className={cn(styles.step, {
                [styles.completed]: step > 3 && !loading,
                [styles.current]: step >= 3,
              })}
            >
              <div className={styles.iconContainer}>
                {step >= 3 &&
                  (loading ? (
                    <Icons.NextArrow className={styles.icon} />
                  ) : (
                    <Icons.CheckCircle className={styles.icon} />
                  ))}
              </div>
              <span>{stepThreeMessage}</span>
            </div>
          </div>
        </div>
      </Grid.Row>
    </Grid>
  );
};

BuildQuote.propTypes = {
  /** Whether or not the loader is still waiting for an operation to complete */
  loading: PropTypes.bool,
  /** Copy text */
  title: PropTypes.string,
  stepOneMessage: PropTypes.string,
  stepTwoMessage: PropTypes.string,
  stepThreeMessage: PropTypes.string,
};

BuildQuote.defaultProps = {
  loading: true,
  title: '',
  stepOneMessage: '',
  stepTwoMessage: '',
  stepThreeMessage: '',
};

export default BuildQuote;
